import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import { useVendorCount } from '@context/VendorContext/useVendorCount';
import { useCateringLeadsStatus } from '@features/events/hooks/catering-leads/useCateringLeadsStatus';
import { useSearchCateringLeads } from '@features/events/hooks/catering-leads/useSearchCateringLeads';
import { useEventLeadsStatus } from '@features/events/hooks/event-leads/useEventLeadsStatus';
import { useSearchEventLeads } from '@features/events/hooks/event-leads/useSearchEventLeads';
import { useSearchVenueLeads } from '@features/events/hooks/venue-leads/useSearchVenueLeads';
import { useVenueLeadsStatus } from '@features/events/hooks/venue-leads/useVenueLeadsStatus';
import { useGetSiteProfile } from '@features/settings/organization-site/hooks/useGetSiteProfile';
import { useOrganizationHasFeatureAccess } from '@hooks/useFeatureAccess';
import useOrganizationStripeStatus from '@hooks/useOrganizationStripeStatus';
import useVendorProductGroups from '@hooks/useVendorProductGroups';

import {
  OnboardingTaskId,
  type OnboardingTaskStatus,
  type OnboardingTaskStatusMap,
} from '../components/onboarding-widget/types';

export const useOnboardingChecks = (
  setTaskStatuses: Dispatch<SetStateAction<OnboardingTaskStatusMap>>
) => {
  const { vendor } = useVendor();
  const hasEventLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.eventLeads,
    config.features.eventLeadsLegacy
  );
  const hasCateringLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.cateringLeads
  );
  const hasVenueLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.venueLeadsFoodTrucks,
    config.features.venueLeadsCatering
  );
  const hasOnlineOrderingAccess = useOrganizationHasFeatureAccess(
    config.features.onlineOrdering
  );
  const hasPosAccess = useOrganizationHasFeatureAccess(config.features.pos);
  const hasQuotesAccess = useOrganizationHasFeatureAccess(
    config.features.quotes
  );
  const hasWebsiteAccess = useOrganizationHasFeatureAccess(
    config.features.website
  );

  const { isLoading: isPinnedEventLeadsLoading, total: totalPinnedEventLeads } =
    useSearchEventLeads(
      vendor.organizationId,
      {
        returnPinnedOnly: true,
        rows: 0,
      },
      {
        enabled: hasEventLeadsAccess,
      }
    );
  const {
    isLoading: isPinnedCateringLeadsLoading,
    total: totalPinnedCateringLeads,
  } = useSearchCateringLeads(
    vendor.organizationId,
    {
      returnPinnedOnly: true,
      rows: 0,
    },
    {
      enabled: hasCateringLeadsAccess,
    }
  );
  const { isLoading: isPinnedVenueLeadsLoading, total: totalPinnedVenueLeads } =
    useSearchVenueLeads(
      vendor.organizationId,
      {
        returnPinnedOnly: true,
        rows: 0,
      },
      {
        enabled: hasVenueLeadsAccess,
      }
    );

  // Check stripe status
  const { isLoading: stripeStatusLoading, status: orgStripeStatus } =
    useOrganizationStripeStatus({ organizationId: vendor.organizationId });

  // Check catalog
  const { isLoading: isCatalogLoading, productGroups } = useVendorProductGroups(
    { vendorId: vendor.id }
  );
  const catalogIsReady = useMemo(() => {
    return productGroups.some((group) =>
      group.categories.some((category) => category.totalProducts > 0)
    );
  }, [productGroups]);

  // Count schedule
  const { count: totalSchedules, isLoading: isSchedulesLoading } =
    useVendorCount('schedules');

  // Count invoices
  const { count: totalInvoices, isLoading: isInvoicesLoading } =
    useVendorCount('invoices');

  // Count transactions
  const { count: totalTransactions, isLoading: isTransactionsLoading } =
    useVendorCount('orders');

  // Check website
  const { isFetching: isWebsiteLoading } = useGetSiteProfile(
    vendor.organizationId,
    'preview',
    {
      enabled: hasWebsiteAccess,
      onSuccess: (data) => {
        if (data.published) {
          setTaskStatuses((prev) => ({
            ...prev,
            [OnboardingTaskId.Website]: 'pending',
            [OnboardingTaskId.WebsiteDefaultMenu]: data.published.defaultMenuId
              ? 'completed'
              : 'pending',
            [OnboardingTaskId.WebsiteLogo]: data.published.logoImageUrl
              ? 'completed'
              : 'pending',
            [OnboardingTaskId.WebsitePublish]: 'completed',
          }));
        } else {
          setTaskStatuses((prev) => ({
            ...prev,
            [OnboardingTaskId.Website]: 'pending',
            [OnboardingTaskId.WebsiteDefaultMenu]: data.preview.defaultMenuId
              ? 'completed'
              : 'pending',
            [OnboardingTaskId.WebsiteLogo]: data.preview.logoImageUrl
              ? 'completed'
              : 'pending',
            [OnboardingTaskId.WebsitePublish]: 'pending',
          }));
        }
      },
    }
  );

  useEffect(() => {
    if (hasOnlineOrderingAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.OnlineOrdering]: 'completed',
        [OnboardingTaskId.CreateTransaction]:
          totalTransactions && totalTransactions > 0 ? 'completed' : 'pending',
      }));
    }

    if (hasPosAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.Pos]: 'completed',
        [OnboardingTaskId.CreateTransaction]:
          totalTransactions && totalTransactions > 0 ? 'completed' : 'pending',
      }));
    }

    if (hasQuotesAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.Quotes]: 'completed',
        [OnboardingTaskId.CreateInvoice]:
          totalInvoices && totalInvoices > 0 ? 'completed' : 'pending',
      }));
    }

    if (hasOnlineOrderingAccess || hasPosAccess || hasQuotesAccess) {
      let stripeStatus: OnboardingTaskStatus = orgStripeStatus?.detailsSubmitted
        ? 'pending'
        : 'hidden';
      stripeStatus = orgStripeStatus?.chargesEnabled
        ? 'completed'
        : stripeStatus;

      let setTaxSettingsStatus: OnboardingTaskStatus =
        orgStripeStatus?.taxSettingsSubmitted ? 'completed' : 'pending';
      setTaxSettingsStatus = orgStripeStatus?.detailsSubmitted
        ? setTaxSettingsStatus
        : 'hidden';

      const createCatalogStatus: OnboardingTaskStatus = catalogIsReady
        ? 'completed'
        : 'pending';

      const setScheduleStatus: OnboardingTaskStatus =
        totalSchedules && totalSchedules > 0 ? 'completed' : 'pending';

      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.CreateCatalog]: createCatalogStatus,
        [OnboardingTaskId.SetSchedule]: setScheduleStatus,
        [OnboardingTaskId.SetTaxSettings]: setTaxSettingsStatus,
        [OnboardingTaskId.StripeConnect]: stripeStatus,
      }));
    }

    if (hasCateringLeadsAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.CateringLeadPinned]:
          totalPinnedCateringLeads > 0 ? 'completed' : 'pending',
      }));
    }
    if (hasEventLeadsAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.EventLeadPinned]:
          totalPinnedEventLeads > 0 ? 'completed' : 'pending',
      }));
    }
    if (hasVenueLeadsAccess) {
      setTaskStatuses((prev) => ({
        ...prev,
        [OnboardingTaskId.VenueLeadPinned]:
          totalPinnedVenueLeads > 0 ? 'completed' : 'pending',
      }));
    }
  }, [
    catalogIsReady,
    hasCateringLeadsAccess,
    hasEventLeadsAccess,
    hasVenueLeadsAccess,
    hasOnlineOrderingAccess,
    hasPosAccess,
    hasQuotesAccess,
    orgStripeStatus?.chargesEnabled,
    orgStripeStatus?.detailsSubmitted,
    orgStripeStatus?.taxSettingsSubmitted,
    setTaskStatuses,
    totalInvoices,
    totalPinnedCateringLeads,
    totalPinnedEventLeads,
    totalPinnedVenueLeads,
    totalSchedules,
    totalTransactions,
  ]);

  const { isLoading: isCateringLeadStatusLoading } = useCateringLeadsStatus(
    vendor.organizationId,
    {
      enabled: hasCateringLeadsAccess,
      onSuccess: (data) => {
        if (data.isRunning) {
          setTaskStatuses((prev) => ({
            ...prev,
            'catering-leads': 'pending',
            'catering-leads-generating': 'pending',
          }));
        } else {
          setTaskStatuses((prev) => ({
            ...prev,
            'catering-leads': 'completed',
            'catering-leads-generating': 'completed',
            'catering-leads-review': 'pending',
          }));
        }
      },
    }
  );
  const { isLoading: isEventLeadStatusLoading } = useEventLeadsStatus(
    vendor.organizationId,
    {
      enabled: hasEventLeadsAccess,
      onSuccess: (data) => {
        if (data.isRunning) {
          setTaskStatuses((prev) => ({
            ...prev,
            'event-leads': 'pending',
            'event-leads-generating': 'pending',
          }));
        } else {
          setTaskStatuses((prev) => ({
            ...prev,
            'event-leads': 'completed',
            'event-leads-generating': 'completed',
            'event-leads-review': 'pending',
          }));
        }
      },
    }
  );
  const { isLoading: isVenueLeadStatusLoading } = useVenueLeadsStatus(
    vendor.organizationId,
    {
      enabled: hasVenueLeadsAccess,
      onSuccess: (data) => {
        if (data.isRunning) {
          setTaskStatuses((prev) => ({
            ...prev,
            'venue-leads': 'pending',
            'venue-leads-generating': 'pending',
          }));
        } else {
          setTaskStatuses((prev) => ({
            ...prev,
            'venue-leads': 'completed',
            'venue-leads-generating': 'completed',
            'venue-leads-review': 'pending',
          }));
        }
      },
    }
  );

  const isLoading =
    isCatalogLoading ||
    isCateringLeadStatusLoading ||
    isEventLeadStatusLoading ||
    isInvoicesLoading ||
    isPinnedCateringLeadsLoading ||
    isPinnedEventLeadsLoading ||
    isPinnedVenueLeadsLoading ||
    isSchedulesLoading ||
    isTransactionsLoading ||
    isVenueLeadStatusLoading ||
    isWebsiteLoading ||
    stripeStatusLoading;
  return { isLoading };
};
